import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { BackLink } from '@/components/back-link';
import { api } from '@/lib/api';
import { fmtAgo } from '@/lib/format';
import { StyleForm } from './style-form';
import { ExampleToggle } from './example-toggle';
import { LinksEditor, type BrandLink } from './links-editor';

type Style = {
  id: string;
  name: string;
  description: string | null;
  guidelines: string | null;
  system: boolean;
  templates: Array<{ id: string; name: string; kind: string; isExample: boolean; updatedAt: string; currentVersion: number }>;
  links: BrandLink[];
};

export async function styleLoader({ params }: LoaderFunctionArgs) {
  return api<Style>(`/styles/${params.id}`);
}

export function StylePage() {
  const style = useLoaderData<typeof styleLoader>();
  return (
    <div className="space-y-5">
      <BackLink to="/templates/styles">Style library</BackLink>
      <div>
        <h1 className="text-2xl">{style.name}</h1>
        <p className="text-sm text-muted-foreground">
          {style.templates.length} template(s) · {style.templates.filter((t) => t.isExample).length} used as AI examples (newest 3 are sent to the model). Assign templates to this style from the template's Details card.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1fr_420px]">
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="data-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Kind</th>
                <th>Updated</th>
                <th>AI example</th>
              </tr>
            </thead>
            <tbody>
              {style.templates.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-muted-foreground">
                    No templates in this style yet — generate one with this style, or assign an existing template in its Details card.
                  </td>
                </tr>
              )}
              {style.templates.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/templates/${t.id}`} className="ff-link">
                      {t.name}
                    </Link>
                    <span className="ml-2 text-xs text-muted-foreground">v{t.currentVersion}</span>
                  </td>
                  <td>{t.kind}</td>
                  <td className="whitespace-nowrap text-muted-foreground">{fmtAgo(t.updatedAt)}</td>
                  <td>
                    <ExampleToggle templateId={t.id} isExample={t.isExample} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <StyleForm key={style.id} initial={style} />
      </div>
      <LinksEditor links={style.links} styleId={style.id} title={`Important links for "${style.name}" (in addition to the global links)`} />
    </div>
  );
}
